import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ArrowDownIcon, SearchIcon } from "components/icons";
import Image from "components/Image";
import Input from "components/Input";
import TruncateText from "components/TruncateText";

import { useBusinessUnitStore } from "@type/store";
import { useTranslationContext } from "context/TranslationContext";
import { useOutsideClick } from "hooks/useOutsideClick";
import cn from "lib/cn";

import { MAIN_URL } from "@type/config";

import styles from "./BUSelectore.module.css";

interface BUSelectorProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
}

const BUSelector = ({
  sidebarCollapsed,
  setSidebarCollapsed,
}: BUSelectorProps) => {
  const { t } = useTranslationContext();
  const selectedBU = useBusinessUnitStore((state) => state.selectedBU);
  const mapBUList = useBusinessUnitStore((state) => state.mapBUList);
  const isLoadingBUList = useBusinessUnitStore(
    (state) => state.isLoadingBUList
  );
  const refDiv = useRef(null);
  const [openList, setOpenList] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const filteredList = useMemo(() => {
    const arrList = Object.keys(mapBUList || {})
      .map((key) => mapBUList?.[key])
      .filter((bu) => !!bu);
    if (!searchKeyword) return arrList;

    return arrList.filter((bu) =>
      bu?.name?.toLowerCase()?.includes(searchKeyword?.toLowerCase())
    );
  }, [mapBUList, searchKeyword]);

  const handleOpenList = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
    }

    setOpenList((prev) => !prev);
  };

  const handleClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleChangeSearchKeyword = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchKeyword(event.target.value);
  };

  const handleSelectBU = (buUniqueId: string) => {
    setOpenList(false);
    setSidebarCollapsed(true);

    setTimeout(() => {
      window.location.href = `${MAIN_URL}/${buUniqueId}`;
    }, 500);
  };

  useOutsideClick(refDiv, () => {
    setOpenList(false);
  });

  useEffect(() => {
    if (sidebarCollapsed) {
      setOpenList(false);
    }
  }, [sidebarCollapsed]);

  return (
    <div className="w-full ">
      <div
        className="flex items-center justify-between px-4 py-3 border-y border-primary-900 cursor-pointer gap-3"
        onClick={handleOpenList}
      >
        <div className="flex items-center items-start gap-4">
          <div className="-ml-[0.06rem] pr-[0.12rem]">
            <Image
              imageUrl={selectedBU?.profilePicture}
              backgroundColor="#003890"
              label={selectedBU?.name}
              width="2rem"
              height="2rem"
              className="flex items-center justify-center border border-white font-semibold"
            />
          </div>

          <div className="text-base w-[10rem]">
            <TruncateText
              id="sidebar-selected-bu"
              text={selectedBU?.name || ""}
              tooltipWidth="17.4rem"
              tooltipPlace="right-end"
            />
          </div>
        </div>
        <div className="pr-2">
          <ArrowDownIcon
            className={cn(styles.arrow, openList ? styles.open : styles.close)}
          />
        </div>
      </div>

      {openList && (
        <div className="relative w-full px-2">
          <div className="absolute w-full">
            <div className="w-full pt-1 pr-4">
              <div className="bg-hsBlue-800 w-full border border-hsBlue-700 rounded-md">
                <div className="p-2 pb-4">
                  <Input
                    leftIcon={<SearchIcon width={18} height={18} />}
                    placeholder={t("search")}
                    inputClassName="pl-8"
                    className="bg-white rounded-lg text-gray-900"
                    value={searchKeyword}
                    onClick={handleClickInput}
                    onChange={handleChangeSearchKeyword}
                  />
                </div>

                <div className="flex flex-col gap-2 max-h-[31.5vh] overflow-y-auto">
                  {isLoadingBUList ? (
                    <div className="px-2 pb-3">{t("loading")} </div>
                  ) : searchKeyword && !filteredList.length ? (
                    <div className="px-2 pb-3">{t("no_result_found")} </div>
                  ) : !filteredList.length ? (
                    <div className="px-2 pb-3"> {t("no_data_available")} </div>
                  ) : (
                    filteredList.map((bu, index) => (
                      <button
                        key={index}
                        className="flex items-center gap-3 border-b border-hsBlue-700 px-2 pb-2"
                        onClick={() => handleSelectBU(bu?.slug || "")}
                      >
                        <div className="pl-[0.18rem]">
                          <Image
                            imageUrl={bu?.profilePicture}
                            backgroundColor="#AA4E05"
                            label={bu?.name}
                            width="2rem"
                            height="2rem"
                            className="flex items-center justify-center border border-white font-semibold"
                          />
                        </div>

                        <div className="text-base text-left w-[12rem]">
                          <TruncateText
                            id={bu?.idUnique || ""}
                            text={bu?.name || ""}
                            tooltipWidth="17.4rem"
                            tooltipPlace="right-end"
                          />
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BUSelector;
